import L, { LatLng, LeafletMouseEvent, Marker as TMarker } from 'leaflet';
import React, { useEffect, useMemo, useState } from 'react';
import { Marker, useMapEvents } from 'react-leaflet';

import { Button, FormContextData, HeroIcons, Type, Variant } from '../../../ComponentLibrary/src';
import { useFormControlValidation } from '../../../ComponentLibrary/src/util/hooks';

export const ClearLatLng = function ClearLatLng({ label }: { label: string }): JSX.Element {
  const { handleOnChange } = useFormControlValidation<string>({
    id: '',
    onChange: (_?: string, formContext?: FormContextData) => {
      formContext?.updateFormState('latitude', undefined);
      formContext?.updateFormState('longitude', undefined);
      formContext?.updateFormState('siteLocation', undefined);
    },
    skipRegister: true,
    validateOnChange: false,
  });

  return (
    <Button type={Type.button} onClick={() => handleOnChange('')} data-pwid="unset-location-button">
      {label}
    </Button>
  );
};

export const GetGps = function GetGps({ markerRef }: { markerRef: React.RefObject<TMarker> }): JSX.Element {
  const { handleOnChange } = useFormControlValidation<GeolocationPosition>({
    id: '',
    onChange: (pos?: GeolocationPosition, formContext?: FormContextData) => {
      if (!pos) return;

      formContext?.updateFormState('latitude', pos.coords.latitude);
      formContext?.updateFormState('longitude', pos.coords.longitude);
      formContext?.updateFormState('siteLocation', undefined);

      markerRef.current?.setLatLng(new L.LatLng(pos.coords.latitude, pos.coords.longitude));
    },
    skipRegister: true,
    validateOnChange: false,
  });

  const handleGetGPS = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(handleOnChange, (err) => alert(err.message), {
      enableHighAccuracy: true,
    });
  };

  return (
    <Button
      type={Type.button}
      onClick={handleGetGPS}
      icon={HeroIcons.StatusOnlineIcon}
      variant={Variant.secondary}
    ></Button>
  );
};

export const MapMarker = function ({
  markerRef,
  map,
  latitude,
  longitude,
}: {
  markerRef: React.RefObject<TMarker>;
  map: L.Map | null;
  latitude?: number;
  longitude?: number;
}): JSX.Element {
  const [dragging, setDragging] = useState(false);
  const { handleOnChange } = useFormControlValidation<LatLng>({
    id: '',
    onChange: (pos?: LatLng, formContext?: FormContextData) => {
      if (!pos) return;

      formContext?.updateFormState('latitude', pos.lat);
      formContext?.updateFormState('longitude', pos.lng);
      formContext?.updateFormState('siteLocation', undefined);
    },
    skipRegister: true,
    validateOnChange: false,
  });

  const getLtLn = (): { lat: number; lng: number } => {
    return markerRef.current?.getLatLng() ?? { lat: latitude ?? 0, lng: longitude ?? 0 };
  };

  useEffect(() => {
    if (latitude && longitude) {
      markerRef.current?.setLatLng(new L.LatLng(latitude, longitude));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;

        if (marker != null) handleOnChange(marker.getLatLng());
        setDragging(false);
      },
      dragstart() {
        setDragging(true);
      },
      // update the map view when the marker location changes
      move() {
        const marker = markerRef.current;
        if (map && marker && !dragging) {
          map.setView(marker.getLatLng(), map.getZoom());
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [map, dragging],
  );

  return <Marker ref={markerRef} position={getLtLn()} draggable eventHandlers={eventHandlers} />;
};

export const MapEvents = function MapEvents({
  clickToSet,
  markerRef,
  setClickToSet,
}: {
  clickToSet: boolean;
  markerRef: React.RefObject<TMarker>;
  setClickToSet: (clickToSet: boolean) => void;
}): JSX.Element {
  const { handleOnChange } = useFormControlValidation<LeafletMouseEvent>({
    id: '',
    onChange: (event?: LeafletMouseEvent, formContext?: FormContextData) => {
      formContext?.updateFormState('latitude', event?.latlng.lat);
      formContext?.updateFormState('longitude', event?.latlng.lng);
      formContext?.updateFormState('siteLocation', undefined);
      if (event) {
        markerRef.current?.setLatLng(new L.LatLng(event.latlng.lat, event.latlng.lng));
      }
    },
    skipRegister: true,
    validateOnChange: false,
  });

  const map = useMapEvents({
    click(event) {
      if (clickToSet) {
        handleOnChange(event);
        setClickToSet(false);
      }
    },
  });

  if (clickToSet) {
    map.getContainer().style.cursor = 'crosshair';
  } else {
    map.getContainer().style.cursor = 'grab';
  }
  return <></>;
};
